import { useCallback } from 'react';
import { formatDate } from '../core/utils';
import { useLanguage } from '../core/i18n';
import { useTimezone } from './useTimezone';

/**
 * Returns a memoised `(value) => string` that formats an ISO date through
 * `Intl.DateTimeFormat` using the current `useLanguage()` locale and the
 * `useTimezone()` user preference. When timezone is empty (default), the
 * browser's local zone is used.
 *
 * Adopters call `formatLocalized(iso)` instead of `formatDate(iso, locale, timezone)`
 * — saves passing both deps explicitly through every call site.
 */
export function useFormattedDate(): (value: string | null | undefined) => string {
  const { language } = useLanguage();
  const [timezone] = useTimezone();
  return useCallback(
    (value) => formatDate(value, language, timezone || undefined),
    [language, timezone],
  );
}
